"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Account = void 0;
const const_1 = require("../../../const");
const utils_1 = require("../../../utils");
const types_1 = require("../types");
exports.Account = {
    _namespace_: "account",
    async info(accountId) {
        const message = await this.call("account.info", new Map([[0, accountId]]));
        return getAccountInfo(message);
    },
    async submitMultisigTxn(txnType, txnData, { nonce } = { nonce: (0, utils_1.makeRandomBytes)(16) }) {
        const m = new Map();
        m.set(0, txnData.from);
        m.set(2, makeSubmittedTxnData(txnType, txnData));
        (txnData === null || txnData === void 0 ? void 0 : txnData.threshold) && m.set(3, txnData.threshold);
        (txnData === null || txnData === void 0 ? void 0 : txnData.expireInSecs) && m.set(4, txnData.expireInSecs);
        typeof (txnData === null || txnData === void 0 ? void 0 : txnData.executeAutomatically) === "boolean" &&
            m.set(5, txnData.executeAutomatically);
        (txnData === null || txnData === void 0 ? void 0 : txnData.memo) && m.set(7, txnData.memo);
        const msg = await this.call("account.multisigSubmitTransaction", m, {
            nonce,
        });
        return getMultisigToken(msg);
    },
    async multisigInfo(token) {
        const res = await this.call("account.multisigInfo", new Map([[0, token]]));
        return await getMultisigInfo(res);
    },
    async multisigApprove(token) {
        return await this.call("account.multisigApprove", new Map([[0, token]]));
    },
    async multisigRevoke(token) {
        return await this.call("account.multisigRevoke", new Map([[0, token]]));
    },
    async multisigExecute(token) {
        return await this.call("account.multisigExecute", new Map([[0, token]]));
    },
    async multisigWithdraw(token) {
        return await this.call("account.multisigWithdraw", new Map([[0, token]]));
    },
    async multisigSetDefaults({ account, threshold, expireInSecs, executeAutomatically, }) {
        const m = new Map()
            .set(0, account)
            .set(1, threshold)
            .set(2, expireInSecs)
            .set(3, executeAutomatically);
        const res = await this.call("account.multisigSetDefaults", m);
        return res.getPayload();
    },
    async create({ name, roles, features }, { nonce } = { nonce: (0, utils_1.makeRandomBytes)(16) }) {
        var _a;
        if (!features)
            throw new Error("Minimum of one feature is required");
        const m = new Map().set(2, features);
        name && m.set(0, name);
        roles && m.set(1, roles);
        const message = (await this.call("account.create", m, { nonce }));
        const decoded = message.getPayload();
        const address = (_a = decoded === null || decoded === void 0 ? void 0 : decoded.get(0)) === null || _a === void 0 ? void 0 : _a.toString();
        return {
            address,
        };
    },
    async setDescription(account, description) {
        const res = await this.call("account.setDescription", new Map().set(0, account).set(1, description));
        return res.getPayload();
    },
    async addRoles(account, roles) {
        const res = await this.call("account.addRoles", new Map().set(0, account).set(1, roles));
        return res === null || res === void 0 ? void 0 : res.getPayload();
    },
    async removeRoles(account, roles) {
        const res = await this.call("account.removeRoles", new Map().set(0, account).set(1, roles));
        return res === null || res === void 0 ? void 0 : res.getPayload();
    },
    async addFeatures({ account, roles, features }) {
        if (!features)
            throw new Error("Minimum of one feature is required");
        const m = new Map().set(0, account).set(2, features);
        roles && m.set(1, roles);
        const res = await this.call("account.addFeatures", m);
        return res.getPayload();
    },
};
async function getMultisigInfo(msg) {
    var _a, _b;
    const result = {
        info: undefined,
    };
    const content = msg.getPayload();
    if (content) {
        try {
            result.info = {
                transaction: await (0, utils_1.makeTxnData)(content.get(1), {
                    isTxnParamData: true,
                }),
                submitter: (_a = content.get(2)) === null || _a === void 0 ? void 0 : _a.toString(),
                approvers: await (async function () {
                    const result = new Map();
                    for (let approver of Array.from(content.get(3))) {
                        const [address, hasApproved] = approver;
                        result.set(address.toString(), hasApproved.get(0));
                    }
                    return result;
                })(),
                threshold: content.get(4),
                executeAutomatically: content.get(5),
                expireDate: (_b = content.get(6)) === null || _b === void 0 ? void 0 : _b.value,
                state: types_1.MultisigTransactionState[content.get(8)],
                memo: content.get(9),
            };
        }
        catch (e) {
            console.error("error in multisig txn data:", e);
        }
    }
    return result;
}
function getMultisigToken(msg) {
    const res = {
        token: undefined,
    };
    const decoded = msg.getPayload();
    if (decoded) {
        res.token = decoded.get(0);
    }
    return res;
}
function makeSubmittedTxnData(txnType, txnData) {
    const accountMultisigTxn = new Map();
    let txnParam;
    const txnTypeIndices = const_1.eventTypeNameToIndices[txnType];
    if (txnType === types_1.EventType.send) {
        txnParam = (0, utils_1.makeLedgerSendParam)(txnData);
    }
    if (txnTypeIndices && txnParam) {
        accountMultisigTxn.set(0, txnTypeIndices);
        accountMultisigTxn.set(1, txnParam);
        return accountMultisigTxn;
    }
    throw new Error(`transaction type not yet implemented: ${txnType}`);
}
function getAccountInfo(message) {
    var _a, _b;
    let result = {
        accountInfo: undefined,
    };
    const payload = message.getPayload();
    if (payload instanceof Map) {
        result.accountInfo = Object.assign({}, (0, utils_1.makeAccountInfoData)({
            description: payload.get(types_1.AccountInfoPayloadResponseLabels.name),
            roles: (_a = payload === null || payload === void 0 ? void 0 : payload.get) === null || _a === void 0 ? void 0 : _a.call(payload, types_1.AccountInfoPayloadResponseLabels.roles),
            features: (_b = payload === null || payload === void 0 ? void 0 : payload.get) === null || _b === void 0 ? void 0 : _b.call(payload, types_1.AccountInfoPayloadResponseLabels.features),
        }));
    }
    return result;
}
